import {DataCollaborator, OwnerInfo} from 'auth/PermissionConstants';
import {GatewayType} from 'instruments/instrument-gateways/InstrumentGatewayConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

import {ConnectionListingGraphQL} from '../../../graphql-fragments/instruments/__generated/ConnectionListingGraphQL';
import {
  AdapterField,
  InstrumentAdapter,
  InstrumentAdapterConfiguration,
  InstrumentServiceDetails,
} from './ConnectionConstants';

export function connectionURL(connectionId: string): string {
  return `/connections/${connectionId}`;
}

export function createConnection(args: {
  schemaId: string;
  name: string;
  tags: TagUpdateObject;
  gatewayId: string;
  gatewayType: GatewayType;
  dataCatalogFolderId: string | null;
  equipmentIds: Array<string>;
  analysisTemplateIds?: Array<string>;
}): Promise<{id: string}> {
  return net.postJSONPromise({
    url: '/1/api/connections',
    data: args,
  });
}

export function updateConnection(
  connectionId: string,
  data: {
    name?: string;
    tags?: TagUpdateObject;
    gatewayType?: GatewayType;
    dataCatalogFolderId?: string;
    isEnabled?: boolean;
    equipmentIds?: Array<string>;
    analysisTemplateIds?: Array<string>;
  }
): Promise<{id: string}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}`,
    data,
  });
}

export function listConnections(
  schemaId?: string,
  adapterId?: string
): Promise<{connections: Array<ConnectionListingGraphQL>}> {
  return net.getJSONPromise(`/1/api/connections?schemaId=${schemaId ?? ''}&adapterId=${adapterId ?? ''}`);
}

export function updateConnectionOwnableInfo(
  connectionId: string,
  data: {
    collaborators: Array<DataCollaborator>;
    ownerInfo: OwnerInfo;
    owner: string;
  }
): Promise<{}> {
  return net.patchJSONPromise({
    url: `/1/api/connections/${connectionId}/collaborators`,
    data,
  });
}

interface BulkArchiveResponse {
  archivedItemIds: Array<string>;
}

export function bulkArchive(ids: Array<string>, purpose: string): Promise<BulkArchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/connections:bulk-archive',
    data: {ids, purpose},
  });
}

interface BulkUnarchiveResponse {
  unarchivedItemIds: Array<string>;
}

export function bulkUnarchive(ids: Array<string>): Promise<BulkUnarchiveResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/connections:bulk-unarchive',
    data: {ids},
  });
}

export function listInstrumentTypes(): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/instrument_types`);
}

export function listAdapters(
  instrumentType?: string
): Promise<{instrumentAdapters: Array<InstrumentAdapter>}> {
  return net.getJSONPromise(`/1/api/instruments/adapters?instrumentType=${instrumentType}`);
}

export function getAdapter(
  adapterId: string,
  gatewayType?: GatewayType
): Promise<{id: string; fields: Array<AdapterField>; transformDataTypes: {[id: string]: string}}> {
  const data = gatewayType ? {gatewayType} : {};
  return net.getJSONPromise(`/1/api/instruments/adapters/${adapterId}`, data);
}

export function getAdapterConfiguration(): Promise<Array<InstrumentAdapterConfiguration>> {
  return net.getJSONPromise(`/1/api/instruments/adapter-configuration`);
}

export function saveAdapterConfiguration(adapters: Array<InstrumentAdapterConfiguration>): Promise<{}> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/instruments/adapter-configuration`,
    data: {instrumentTypes: adapters},
  });
}

export function getInstrumentServiceDetails(connectionId: string): Promise<InstrumentServiceDetails> {
  return net.getJSONPromise(`/1/api/connections/${connectionId}/info`);
}

export function startCredentialTest(connectionId: string): Promise<{instrumentQueryId: string}> {
  return net.postJSONPromise({
    url: `/1/api/connections/${connectionId}:test-credentials`,
    data: {},
  });
}
